
export type SagaJawaban = boolean | null;

export type SagaKomponenKey = "tampilan" | "napas" | "sirkulasi";

export type SagaKesimpulan =
  | "gagal_jantung_paru"
  | "gagal_napas"
  | "renjatan"
  | "gawat_napas"
  | "gangguan_ssp_metabolik"
  | "stabil";

export type SagaZona = "merah" | "kuning" | "hijau";

export interface SagaItems {
  tampilan_tonus: SagaJawaban;
  tampilan_interaksi: SagaJawaban;
  tampilan_kenyamanan: SagaJawaban;
  tampilan_pandangan: SagaJawaban;
  tampilan_bicara: SagaJawaban;
  napas_suara_tambahan: SagaJawaban;
  napas_posisi_abnormal: SagaJawaban;
  napas_retraksi: SagaJawaban;
  napas_cuping_hidung: SagaJawaban;
  sirkulasi_sianosis: SagaJawaban;
  sirkulasi_pucat: SagaJawaban;
  sirkulasi_kutis_marmorata: SagaJawaban;
}

export type SagaItemKey = keyof SagaItems;

export interface SagaKomponen {
  key: SagaKomponenKey;
  label: string;
  opsiNormal: string;
  opsiAbnormal: string;
  items: { key: SagaItemKey; label: string }[];
}

export const SAGA_KOMPONEN: SagaKomponen[] = [
  {
    key: "tampilan",
    label: "Tampilan",
    opsiNormal: "Normal",
    opsiAbnormal: "Abnormal",
    items: [
      { key: "tampilan_tonus", label: "Tonus" },
      { key: "tampilan_interaksi", label: "Interaksi" },
      { key: "tampilan_kenyamanan", label: "Kenyamanan" },
      { key: "tampilan_pandangan", label: "Pandangan" },
      { key: "tampilan_bicara", label: "Bicara / Menangis" }
    ]
  },
  {
    key: "napas",
    label: "Upaya Napas",
    opsiNormal: "Tidak Ada",
    opsiAbnormal: "Ada",
    items: [
      { key: "napas_suara_tambahan", label: "Suara napas tambahan" },
      { key: "napas_posisi_abnormal", label: "Posisi tubuh abnormal" },
      { key: "napas_retraksi", label: "Retraksi" },
      { key: "napas_cuping_hidung", label: "Napas cuping hidung" }
    ]
  },
  {
    key: "sirkulasi",
    label: "Sirkulasi",
    opsiNormal: "Tidak Ada",
    opsiAbnormal: "Ada",
    items: [
      { key: "sirkulasi_sianosis", label: "Sianosis" },
      { key: "sirkulasi_pucat", label: "Pucat" },
      { key: "sirkulasi_kutis_marmorata", label: "Kutis marmorata" }
    ]
  }
];

export const SAGA_KEYS: SagaItemKey[] = SAGA_KOMPONEN.reduce(
  (acc: SagaItemKey[], komponen) => acc.concat(komponen.items.map(item => item.key)),
  []
);

const LABEL_KESIMPULAN: Record<SagaKesimpulan, string> = {
  gagal_jantung_paru: "Gagal Jantung-Paru",
  gagal_napas: "Gagal Napas",
  renjatan: "Renjatan",
  gawat_napas: "Gawat Napas",
  gangguan_ssp_metabolik: "Gangguan SSP / Metabolik",
  stabil: "Stabil"
};

const ZONA_KESIMPULAN: Record<SagaKesimpulan, SagaZona> = {
  gagal_jantung_paru: "merah",
  gagal_napas: "merah",
  renjatan: "merah",
  gawat_napas: "kuning",
  gangguan_ssp_metabolik: "kuning",
  stabil: "hijau"
};

const WARNA_ZONA: Record<SagaZona, string> = {
  merah: "red darken-1",
  kuning: "amber darken-2",
  hijau: "green darken-1"
};

export function sagaItemsKosong(): SagaItems {
  const items = {} as SagaItems;

  SAGA_KEYS.forEach(key => {
    items[key] = null;
  });

  return items;
}

export function sagaNormalizeItems(raw): SagaItems {
  const src = raw && typeof raw === "object" ? raw : {};
  const items = {} as SagaItems;

  SAGA_KEYS.forEach(key => {
    const val = src[key];

    if (val === null || val === undefined || val === "") {
      items[key] = null;
      return;
    }

    if (typeof val === "string") {
      items[key] = ["0", "false", "no", "tidak"].indexOf(val.toLowerCase()) === -1;
      return;
    }

    items[key] = !!val;
  });

  return items;
}

export function sagaAdaIsian(items: SagaItems): boolean {
  return SAGA_KEYS.some(key => items[key] !== null && items[key] !== undefined);
}

export function sagaLengkap(items: SagaItems): boolean {
  return SAGA_KEYS.every(key => items[key] === true || items[key] === false);
}

export function sagaKomponenAbnormal(items: SagaItems): Record<SagaKomponenKey, boolean> {
  const hasil = {} as Record<SagaKomponenKey, boolean>;

  SAGA_KOMPONEN.forEach(komponen => {
    hasil[komponen.key] = komponen.items.some(item => items[item.key] === true);
  });

  return hasil;
}

export function sagaKesimpulan(items: SagaItems): SagaKesimpulan | null {
  if (!sagaAdaIsian(items)) {
    return null;
  }

  const abnormal = sagaKomponenAbnormal(items);
  const t = abnormal.tampilan;
  const n = abnormal.napas;
  const s = abnormal.sirkulasi;

  if (t && n && s) {
    return "gagal_jantung_paru";
  }

  if (t && n) {
    return "gagal_napas";
  }

  if (s && !n) {
    return "renjatan";
  }

  if (n && !t) {
    return "gawat_napas";
  }

  if (t && !n && !s) {
    return "gangguan_ssp_metabolik";
  }

  return "stabil";
}

export function sagaLabelKesimpulan(kode: SagaKesimpulan | string | null): string {
  return kode && LABEL_KESIMPULAN[kode] ? LABEL_KESIMPULAN[kode] : "";
}

export function sagaZona(kode: SagaKesimpulan | string | null): SagaZona | null {
  return kode && ZONA_KESIMPULAN[kode] ? ZONA_KESIMPULAN[kode] : null;
}

export function sagaWarna(zona: SagaZona | string | null): string {
  return zona && WARNA_ZONA[zona] ? WARNA_ZONA[zona] : "grey";
}
